const STORAGE_KEY = 'nextos:isolated-browser-launch';
const EVENT_NAME = 'nextos:isolated-browser-launch';

export interface IsolatedBrowserLaunch {
  appId: string;
  url: string;
  requestedAt: number;
}

function validLaunch(value: unknown): value is IsolatedBrowserLaunch {
  if (!value || typeof value !== 'object') return false;
  const launch = value as Partial<IsolatedBrowserLaunch>;
  if (!/^[a-z][a-z0-9-]{2,63}$/.test(String(launch.appId || ''))) return false;
  if (!Number.isFinite(launch.requestedAt) || Math.abs(Date.now() - Number(launch.requestedAt)) > 60_000) return false;
  try {
    const target = new URL(String(launch.url || ''));
    if (target.protocol !== 'https:' || target.username || target.password || target.hash) return false;
    return target.origin === 'https://next.my365.cloud' && target.pathname.startsWith('/apps/') && !target.pathname.includes('..');
  } catch {
    return false;
  }
}

export function queueIsolatedBrowserLaunch(appId: string, url: string): void {
  const launch: IsolatedBrowserLaunch = { appId, url, requestedAt: Date.now() };
  if (!validLaunch(launch)) throw new Error('INVALID_ISOLATED_BROWSER_LAUNCH');
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(launch));
  window.dispatchEvent(new CustomEvent<IsolatedBrowserLaunch>(EVENT_NAME, { detail: launch }));
}

export function pendingIsolatedBrowserLaunch(): IsolatedBrowserLaunch | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const launch = JSON.parse(raw) as unknown;
    return validLaunch(launch) ? launch : null;
  } catch {
    return null;
  }
}

export function consumeIsolatedBrowserLaunch(): IsolatedBrowserLaunch | null {
  const launch = pendingIsolatedBrowserLaunch();
  sessionStorage.removeItem(STORAGE_KEY);
  return launch;
}

export function subscribeIsolatedBrowserLaunch(listener: (launch: IsolatedBrowserLaunch) => void): () => void {
  const handler = (event: Event) => {
    const launch = (event as CustomEvent<unknown>).detail;
    if (validLaunch(launch)) listener(launch);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
